/**
 * More options (Option C, C2): what most connections never change — AI access (every engine), Oracle's initial schema
 * and debug protocol, Azure Storage's per-service endpoint overrides. Collapsed by default; while collapsed its heading
 * names what it holds, with the value of anything set away from its default ("AI access: Read-only").
 */
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import { OracleDebugProtocolRow, OracleInitialSchemaRow } from '../../components/OracleConnectionFields';
import { AzureStorageEndpointsRow } from '../../components/AzureStorageEndpointFields';
import { FormRow } from '../pane/FormRow';
import { FormSection } from '../pane/FormSection';
import { SELECT_CLASS, type ConnectionFormApi } from './fieldControls';

type Values = Partial<ConnectionFormValues>;

const AI_ACCESS_OPTIONS = [
  { value: 'inherit', label: 'Use AI Integrations default (recommended)', short: '' },
  { value: 'block', label: 'Block — no AI queries allowed', short: 'Block' },
  { value: 'read-only', label: 'Read-only — SELECT queries only', short: 'Read-only' },
  { value: 'confirm', label: 'Confirm — ask before writes', short: 'Confirm writes' },
] as const;

/** What the collapsed heading says: each row's name, with its value when it differs from the default. */
export function optionsSummary(engine: DatabaseEngine, values: Values): string {
  const parts: string[] = [];
  if (engine === 'oracle') {
    parts.push(values.oracleInitialSchema?.trim() ? `Initial schema: ${values.oracleInitialSchema.trim()}` : 'Initial schema');
    parts.push(values.oracleDebugProtocol === 'jdwp' ? 'Debug protocol: JDWP' : 'Debug protocol');
  }
  if (engine === 'azurestorage') {
    const overridden = !values.azureStorageEmulator && Object.keys(values.azureStorageEndpoints ?? {}).length > 0;
    parts.push(overridden ? 'Endpoints: overridden' : 'Endpoints');
  }
  const ai = AI_ACCESS_OPTIONS.find((option) => option.value === (values.mcpPermissionLevel ?? 'inherit'));
  parts.push(ai?.short ? `AI access: ${ai.short}` : 'AI access');
  return parts.join(' · ');
}

function AiAccessRow({ form }: { form: ConnectionFormApi }) {
  return (
    <form.Field name="mcpPermissionLevel">
      {(field: any) => (
        <FormRow
          row="mcpPermissionLevel"
          label="AI access"
          htmlFor="mcpPermissionLevel"
          hint={
            <>
              What AI assistants connected through MCP may run on this connection.
              {field.state.value === 'confirm' && <span className="mt-1 block text-warning">Write operations will require your approval before they run.</span>}
            </>
          }
        >
          <select
            id="mcpPermissionLevel"
            name="mcpPermissionLevel"
            className={SELECT_CLASS}
            value={field.state.value ?? 'inherit'}
            onChange={(event) => field.handleChange(event.target.value)}
            onBlur={field.handleBlur}
            data-testid="mcp-permission-level-select"
          >
            {AI_ACCESS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </FormRow>
      )}
    </form.Field>
  );
}

export function OptionsSection({ form, engine }: { form: ConnectionFormApi; engine: DatabaseEngine }) {
  return (
    <form.Subscribe selector={(state: { values: Values }) => optionsSummary(engine, state.values)}>
      {(summary: string) => (
        <FormSection id="more_options" title="More options" defaultOpen={false} summary={summary}>
          {engine === 'oracle' && (
            <>
              <OracleInitialSchemaRow form={form} />
              <OracleDebugProtocolRow form={form} />
            </>
          )}
          {engine === 'azurestorage' && <AzureStorageEndpointsRow form={form} />}
          <AiAccessRow form={form} />
        </FormSection>
      )}
    </form.Subscribe>
  );
}
